"use client";
import React, { useState } from "react";
import { Form, Formik } from "formik";
import { Button } from "@/app/(components)/Basic/Button";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import { NftDetailsListItem } from "../NftDetailsListItem";
import { CurrencyInput } from "@/app/(components)/Basic/CurrencyInput";
import { Nft } from "@/api/nfts/types";
import { BaseSignedOrder, ReserveAuctionOrder } from "@/api/orders/types";

import useAccountBalance from "@/shared/hooks/useAccountBalance";
import usePaymentCurrency from "@/shared/hooks/usePaymentCurrency";
import { useMutation } from "@tanstack/react-query";
import ordersApi from "@/api/orders/client";
import { useWeb3State } from "@/web3/context";
import { getFeePercentage, toLowDenom } from "@/shared/currencies";
import { Type } from "@/app/(components)/Basic/Button/constants";
import useSignedFunction from "@/shared/hooks/useSignedFunction";
import { getTotalPrice } from "@/shared/hooks/useTotalTakerPrice";
import useExchangeERC20ApprovalMutation from "@/web3/contracts/common/actions/useExchangeERC20ApprovalMutation";
import useEIP712Signature from "@/eip712/hooks/useEIP712Signature";
import { getReserveAuctionBidMessage } from "@/eip712";
import { NftModalBase } from "../NftModalBase";
import * as Yup from "yup";
import useWithCorrectNetwork from "@/shared/hooks/useWithCorrectNetwork";
import usePaymentMethods from "@/app/(components)/Basic/CurrencyInput/hooks/usePaymentMethods";
import useReservePrice from "../../Trade/TradeDetails/hooks/useReservePrice";
import { getCryptoInUSD } from "@/shared/hooks/useCryptoInUSD";
import useCurrencyRate from "@/shared/hooks/useCurrencyRate";
import useOrdersInvalidation from "@/shared/hooks/useOrdersInvalidation";

export interface Props {
  nft: Nft;
  auction: BaseSignedOrder;
  onSuccess: () => void;
  onClose: () => void;
}

function NftReserveBidModal({ nft, auction, onSuccess, onClose }: Props) {
  const [isOpen, setIsOpen] = useState(true);
  const { currentAddress } = useWeb3State();
  const currencies = usePaymentMethods(true);
  const paymentErc20TokenAddress = currencies && currencies[0].value;
  const paymentMethod = usePaymentCurrency(paymentErc20TokenAddress);

  const { balanceInCrypto } = useAccountBalance(paymentErc20TokenAddress);
  const { data: usdRate } = useCurrencyRate(paymentMethod?.id);

  const invalidateOrders = useOrdersInvalidation(nft.id);

  const reservePrice = useReservePrice(auction);
  const [price, setPrice] = useState<string>(
    reservePrice?.raw?.toString() || "",
  );
  const totalPrice = getTotalPrice(price, paymentMethod, auction.takerFee);

  const [bidId, setBidId] = useState("");

  const { approve, isLoading: isApproveLoading } =
    useExchangeERC20ApprovalMutation(paymentErc20TokenAddress);

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  const sendMessageForSign = useEIP712Signature({
    onError: async () => {
      await ordersApi.deleteBid(bidId);
      invalidateOrders();
    },
  });

  const { mutateAsync: requestSignature, isLoading: isSignatureLoading } =
    useMutation({
      mutationFn: async (bid: ReserveAuctionOrder) => {
        setBidId(bid.id!);

        const bidTypedData = getReserveAuctionBidMessage(bid);

        const signature = await sendMessageForSign(bidTypedData);

        await ordersApi.updateBidSignature({
          orderId: bid.id!,
          auctionId: auction.id!,
          signature,
        });
      },
      onSuccess: () => {
        invalidateOrders();

        setIsOpen(false);
        onSuccess();
      },
    });

  const { mutateAsync: placeBid, isLoading: isCreateLoading } = useMutation({
    mutationFn: (bid: ReserveAuctionOrder) => ordersApi.placeBid(bid),
    onSuccess: (bid: ReserveAuctionOrder) => {
      requestSignature(bid);
    },
    onError: () => {},
  });

  const checkedPlaceBid = useSignedFunction(placeBid);

  const handleSubmit = async ({ price, paymentErc20TokenAddress }: any) => {
    const rawPrice = toLowDenom(price, paymentMethod?.decimals);

    const isApproved = await approve(BigInt(totalPrice!.raw!));

    if (!isApproved) return;

    await checkedPlaceBid({
      nftContract: nft.contractId,
      tokenId: +nft.tokenId,
      buyer: currentAddress!,
      totalPrice: totalPrice!.raw!,
      price: rawPrice,
      paymentErc20TokenAddress,
      expiresAt: auction.expiresAt,
      seller: nft.owner.address,
      isBid: true,
      makerFee: auction.makerFee,
      takerFee: auction.takerFee,
      royaltiesFee: auction.royaltiesFee,
      royaltiesRecipient: auction.royaltiesRecipient,
      collectiveFee: auction.collectiveFee,
      collectiveRecipient: auction.collectiveRecipient,
    });
  };

  const updatePrice = (value: any) => {
    if (typeof value === "object") return;

    const rawPrice = toLowDenom(value, paymentMethod?.decimals);

    setPrice(rawPrice!);
  };

  const isLoading = isCreateLoading || isApproveLoading || isSignatureLoading;

  const validationSchema = Yup.object().shape({
    price: Yup.number()
      .required("Price is required")
      .min(
        +reservePrice!.rounded!,
        `Min bid is ${reservePrice?.rounded} ${paymentMethod?.label}`,
      )
      .max(+balanceInCrypto, `You don't have enough ${paymentMethod?.label}`),
  });

  const checkSubmitOffer = useWithCorrectNetwork((data: any) =>
    handleSubmit(data),
  );

  return (
    <NftModalBase
      isOpen={isOpen}
      onClose={handleClose}
      disableClose={isLoading}
      title="Place bid"
      nft={nft}
    >
      <Formik
        initialValues={{
          price: reservePrice?.rounded,
          paymentErc20TokenAddress: currencies && currencies[0].value,
        }}
        validateOnChange={true}
        validationSchema={validationSchema}
        onSubmit={checkSubmitOffer}
      >
        {({ values }) => (
          <Form>
            <div className="w-full">
              <CurrencyInput
                name="price"
                label="Your offer"
                shortInfo={`Balance: ${balanceInCrypto} ${paymentMethod?.label}`}
                allThemesWithWhiteBackground
                onlyErc20Support
                disabled={isLoading}
                onChange={updatePrice}
              />
            </div>
            <NftDetailsListItem
              small
              label="Service fee"
              labelClassName="endemic:text-disabled"
            >
              {getFeePercentage(auction.takerFee || 0)}%
            </NftDetailsListItem>
            <NftDetailsListItem
              small
              label="Total amount"
              labelClassName="endemic:text-disabled"
            >
              <div className="flex flex-col sm:flex-row text-right sm:text-left items-center">
                {values.price
                  ? getTotalPrice(
                      toLowDenom(values.price),
                      paymentMethod,
                      auction.takerFee,
                    )?.rounded
                  : null}
                <span className="text-secondary text-body-3 ml-2 endemic:text-disabled">
                  {values.price && usdRate
                    ? `${getCryptoInUSD(
                        getTotalPrice(
                          toLowDenom(values.price),
                          paymentMethod,
                          auction.takerFee,
                        )?.rounded,
                        usdRate.price,
                      )} USD`
                    : "-"}
                </span>
              </div>
            </NftDetailsListItem>
            <div className="flex justify-center mt-10">
              <Button
                element={Type.Button}
                type="button"
                className="mx-2"
                disabled={isLoading}
                onClick={handleClose}
                endIcon={!isLoading && <ArrowRightIcon className="w-4" />}
              >
                Cancel
              </Button>
              <Button
                element={Type.Button}
                type="submit"
                disabled={isLoading}
                className="mx-2"
                endIcon={!isLoading && <ArrowRightIcon className="w-4" />}
              >
                {isLoading ? "Loading" : "Place"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </NftModalBase>
  );
}

export default NftReserveBidModal;
